import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { StatusBadge, fmtDate } from "@/components/common";
import { updateResource } from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);

// Click the "Not Contacted"/"Contacted" badge to record who was contacted and when,
// without opening the full edit form. Used on the Facilities board.
export default function ContactCell({ row, path = "workorders" }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [who, setWho] = useState(row.contacted_who || "");
  const [when, setWhen] = useState(row.contacted_when || today());
  const [saving, setSaving] = useState(false);
  const isContacted = row.contact_status === "contacted";
  const isInternal = row.contact_status === "internal";

  const refresh = () => {
    qc.invalidateQueries({ queryKey: [path] });
    qc.invalidateQueries({ queryKey: ["summary"] });
    qc.invalidateQueries({ queryKey: ["reminders"] });
  };

  const save = async (status) => {
    if (status === "contacted" && !who.trim()) {
      toast.error("Enter who was contacted");
      return;
    }
    setSaving(true);
    try {
      await updateResource(path, row.id, {
        contact_status: status,
        contacted_who: status === "not_contacted" ? "" : who.trim(),
        contacted_when: status === "not_contacted" ? "" : when,
      });
      refresh();
      toast.success(status === "contacted" ? "Marked contacted" : status === "internal" ? "Marked internal" : "Marked not contacted");
      setOpen(false);
    } catch {
      toast.error("Couldn't update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="contact-cell-trigger" className="inline-flex items-start gap-1 group text-left focus:outline-none">
          {isContacted || isInternal ? (
            <span>
              <StatusBadge status={isInternal ? "internal" : "contacted"} />
              <span className="block text-xs text-gray-600 mt-1">
                {[row.contacted_who, row.contacted_when ? fmtDate(row.contacted_when) : ""].filter(Boolean).join(" · ") || "—"}
              </span>
            </span>
          ) : (
            <StatusBadge status="not_contacted" />
          )}
          <ChevronDown className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-900 transition-colors mt-1" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="rounded-none border-2 border-gray-900 w-72">
        <div className="space-y-3">
          <div className="font-head font-bold uppercase tracking-wide text-sm">Repair Contact</div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Who Was Contacted</Label>
            <Input
              data-testid="contact-who-input"
              className="mt-1 rounded-none border-2"
              value={who}
              placeholder="e.g. Anderson HVAC"
              onChange={(e) => setWho(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Date Contacted</Label>
            <Input
              data-testid="contact-when-input"
              type="date"
              className="mt-1 rounded-none border-2"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
          </div>
          <Button
            data-testid="contact-save-btn"
            onClick={() => save("contacted")}
            disabled={saving}
            className="w-full rounded-none bg-[#00C853] hover:bg-green-600 text-white font-bold uppercase"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Mark Contacted
          </Button>
          <Button
            data-testid="contact-internal-btn"
            onClick={() => save("internal")}
            disabled={saving}
            className="w-full rounded-none bg-[#0D1B5E] hover:bg-[#16265F] text-white font-bold uppercase"
          >
            Handled Internally
          </Button>
          {(isContacted || isInternal) && (
            <Button
              data-testid="contact-clear-btn"
              variant="outline"
              onClick={() => save("not_contacted")}
              disabled={saving}
              className="w-full rounded-none border-2"
            >
              Clear (Not Contacted)
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
